import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  where,
  orderBy,
  addDoc,
} from 'firebase/firestore';
import type { DocumentData } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import type { User, CreateUserInput, UpdateUserInput, UserRole, FileMetadata } from '../types/users';
import type { WorkspaceReference, ActivityRecord } from '../types/common';
import { auth } from '../firebase';
import { getStorage, ref, deleteObject } from 'firebase/storage';

const USERS_COLLECTION = 'users';
// Helper function to remove undefined values
const removeUndefined = (obj: DocumentData): DocumentData => {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== undefined)
  );
};

// Helper function to delete profile picture from storage
const deleteProfilePicture = async (metadata: FileMetadata | undefined) => {
  if (!metadata?.path) return;
  
  try {
    const storageRef = ref(getStorage(), metadata.path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting profile picture:', error);
  }
};

export async function getUsers(pageSize: number = 10, lastDoc?: QueryDocumentSnapshot): Promise<{ users: User[]; lastDoc?: QueryDocumentSnapshot }> {
  console.log('[API] getUsers called', { pageSize, lastDoc });
  let q = query(
    collection(db, USERS_COLLECTION),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );

  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }

  const querySnapshot = await getDocs(q);
  const users: User[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    users.push({
      id: doc.id,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      username: data.username,
      status: data.status || 'active',
      roles: data.roles || [],
      contacts: data.contacts || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLogin: data.lastLogin?.toDate(),
      workspace: {
        id: data.workspace.id,
        name: data.workspace.name,
        slug: data.workspace.slug
      },
      language: data.language || 'es',
      currency: data.currency || 'CLP',
      profilePicture: data.profilePicture,
      phoneNumber: data.phoneNumber,
      activityRecords: data.activityRecords || []
    } as User);
  });

  return {
    users,
    lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1]
  };
}

export async function getUserById(id: string): Promise<User | null> {
  console.log('[API] getUserById called', { id });
  const ref = doc(db, USERS_COLLECTION, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    id: snap.id,
    uid: data.uid,
    email: data.email,
    displayName: data.displayName,
    username: data.username,
    status: data.status || 'active',
    roles: data.roles || [],
    contacts: data.contacts || [],
    createdAt: data.createdAt?.toDate() || new Date(),
    lastLogin: data.lastLogin?.toDate(),
    workspace: {
      id: data.workspace.id,
      name: data.workspace.name,
      slug: data.workspace.slug
    },
    language: data.language || 'es',
    currency: data.currency || 'CLP',
    profilePicture: data.profilePicture,
    phoneNumber: data.phoneNumber,
    activityRecords: data.activityRecords || []
  } as User;
}

export async function getUserByEmail(email: string): Promise<User | null> {
  try {
    const q = query(
      collection(db, USERS_COLLECTION),
      where('email', '==', email)
    );
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return {
        id: doc.id,
        uid: data.uid,
        email: data.email,
        displayName: data.displayName,
        username: data.username,
        status: data.status || 'active',
        roles: data.roles || [],
        contacts: data.contacts || [],
        createdAt: data.createdAt?.toDate() || new Date(),
        lastLogin: data.lastLogin?.toDate(),
        workspace: {
          id: data.workspace.id,
          name: data.workspace.name,
          slug: data.workspace.slug
        },
        language: data.language || 'es',
        currency: data.currency || 'CLP',
        profilePicture: data.profilePicture,
        phoneNumber: data.phoneNumber,
        activityRecords: data.activityRecords || []
      } as User;
    }
    return null;
  } catch (error) {
    console.error('[API] Error fetching user by email:', error);
    throw error;
  }
}

export const addUser = async (userData: CreateUserInput): Promise<User> => {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userData.email,
      userData.password
    );

    const now = new Date();
    
    // Create activity record for user creation
    const activityRecord: ActivityRecord = {
      timestamp: now,
      type: 'created',
      performedBy: {
        id: userCredential.user.uid,
        displayName: userData.displayName
      },
      details: {
        field: 'user',
        newValue: {
          email: userData.email,
          displayName: userData.displayName,
          username: userData.username,
          status: userData.status,
          workspace: userData.workspace
        }
      }
    };

    const userToCreate = removeUndefined({
      uid: userCredential.user.uid,
      email: userData.email,
      displayName: userData.displayName,
      username: userData.username,
      status: userData.status,
      contacts: userData.contacts || [],
      roles: userData.roles,
      workspace: {
        id: userData.workspace.id,
        name: userData.workspace.name,
        slug: userData.workspace.slug
      },
      language: userData.language || 'es',
      currency: userData.currency || 'CLP',
      profilePicture: userData.profilePicture,
      phoneNumber: userData.phoneNumber,
      createdAt: now,
      lastLogin: now,
      activityRecords: [activityRecord]
    });

    // Create Firestore user document
    const usersRef = collection(db, USERS_COLLECTION);
    const docRef = await addDoc(usersRef, userToCreate);

    return {
      id: docRef.id,
      ...userToCreate
    } as User;
  } catch (error: any) {
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('USER_EXISTS');
    }
    throw error;
  }
};

export async function attachUserToWorkspace(userId: string, workspaceData: WorkspaceReference): Promise<void> {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      workspace: workspaceData,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('[API] Error attaching user to workspace:', error);
    throw error;
  }
}

export const updateUser = async (id: string, userData: UpdateUserInput): Promise<void> => {
  try {
    // If updating profile picture, delete the old one
    if (userData.profilePicture) {
      const currentUser = await getUserById(id);
      if (currentUser?.profilePicture && 
          currentUser.profilePicture.path !== userData.profilePicture.path) {
        await deleteProfilePicture(currentUser.profilePicture);
      }
    }

    const userRef = doc(db, USERS_COLLECTION, id);
    await updateDoc(userRef, removeUndefined({
      ...userData,
      updatedAt: new Date()
    }));
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const deleteUser = async (id: string): Promise<void> => {
  console.log('[API] deleteUser called', { id });
  try {
    // Get user data to delete profile picture
    const user = await getUserById(id);
    if (user?.profilePicture) {
      await deleteProfilePicture(user.profilePicture);
    }

    // Delete Firestore document
    const ref = doc(db, USERS_COLLECTION, id);
    await deleteDoc(ref);
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

export const getUsersByRole = async (role: UserRole): Promise<User[]> => {
  const usersRef = collection(db, USERS_COLLECTION);
  const q = query(usersRef, where('roles', 'array-contains', role));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      status: data.status || 'active',
      roles: data.roles || [],
      contacts: data.contacts || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLogin: data.lastLogin?.toDate(),
      workspace: {
        id: data.workspace.id,
        name: data.workspace.name,
        slug: data.workspace.slug
      },
      language: data.language || 'es',
      currency: data.currency || 'CLP',
      profilePicture: data.profilePicture,
      phoneNumber: data.phoneNumber,
      activityRecords: data.activityRecords || []
    } as User;
  });
};

export const getUsersByWorkspace = async (workspaceId: string): Promise<User[]> => {
  const usersRef = collection(db, USERS_COLLECTION);
  const q = query(usersRef, where('workspace.id', '==', workspaceId));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      id: doc.id,
      uid: data.uid,
      email: data.email,
      displayName: data.displayName,
      status: data.status || 'active',
      roles: data.roles || [],
      contacts: data.contacts || [],
      createdAt: data.createdAt?.toDate() || new Date(),
      lastLogin: data.lastLogin?.toDate(),
      workspace: {
        id: data.workspace.id,
        name: data.workspace.name,
        slug: data.workspace.slug
      },
      language: data.language || 'es',
      currency: data.currency || 'CLP',
      profilePicture: data.profilePicture,
      phoneNumber: data.phoneNumber,
      activityRecords: data.activityRecords || []
    } as User;
  });
}; 